import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useCollaborations } from '@/hooks/useCollaborations'
import { sports } from '@/config'
import { Menu, X, ChevronRight } from 'lucide-react'
import { UserMenuContent } from './UserMenuContent'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { useState, useEffect } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useGetUnreadCountQuery } from '@/redux/messagesApi'
import { athleteNavigationItems } from './athleteNavigation'

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { bids } = useCollaborations()
  const { athlete, logout } = useAuth()
  const navigate = useNavigate()
  const hasNewBids = bids.length > 0
  const { data: unreadData } = useGetUnreadCountQuery()
  const unreadMessageCount = unreadData?.unreadCount ?? 0
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768)
  const location = useLocation()
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Get sport display name(s)
  const getSportDisplay = () => {
    if (!athlete?.sports || athlete.sports.length === 0) {
      return 'No sport'
    }

    // Look up sport names from config using the IDs
    const sportNames = athlete.sports.map((sportId) => {
      const sport = sports.find((s) => s.id === sportId)
      return sport?.name || `Unknown (${sportId})`
    })

    return sportNames.join(', ')
  }

  // Handle navigation item click to expand sidebar
  const handleNavClick = () => {
    if (!isOpen && isDesktop) {
      toggleSidebar()
    }
  }

  const mainNavigationItems = athleteNavigationItems.filter((item) => item.section !== 'secondary')
  const secondaryNavigationItems = athleteNavigationItems.filter(
    (item) => item.section === 'secondary',
  )

  // Active route styles consistency
  const getActiveStyle = (isActive) => {
    return isActive ? 'bg-[#C0FD72] text-black' : 'text-gray-300 hover:bg-white/5 hover:text-white'
  }
  return (
    // Replace the entire aside element with this updated version
    <aside
      className={`
    ${isOpen ? 'w-full md:w-64' : 'w-16'} 
    ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'} 
    bg-white/15 backdrop-blur-xs transition-all duration-300 overflow-hidden 
    z-10 h-screen flex flex-col fixed top-0 left-0 bottom-0
  `}
    >
      <div className="p-4 border-b border-white/10 flex justify-between items-center">
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-md hover:bg-white/10 flex items-center justify-center text-white transition-transform duration-300"
          aria-label="Toggle sidebar"
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <ScrollArea className="flex-1 overflow-y-auto">
        <nav className="flex-1">
          <ul className="space-y-0.5">
            {mainNavigationItems.map((item) => {
              const Icon = item.icon
              const baseItemClass = `flex items-center ${isOpen ? 'gap-3' : 'justify-center'} px-3 py-2 w-full`

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `${baseItemClass} ${getActiveStyle(isActive)}`}
                    onClick={handleNavClick}
                    end={item.end}
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative shrink-0">
                          <Icon
                            className={
                              isActive ? 'h-6 w-6 text-black' : `h-6 w-6 text-[${item.color}]`
                            }
                            style={isActive ? undefined : { color: item.color }}
                          />
                          {item.showUnreadBadge && unreadMessageCount > 0 && (
                            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[#C0FD72] border-2 border-[#1a1f2e]" />
                          )}
                        </span>
                        {isOpen && (
                          <span className="flex items-center gap-2 transition-opacity">
                            {item.label}
                            {item.showUnreadBadge && unreadMessageCount > 0 && (
                              <span className="text-[10px] font-bold bg-[#C0FD72] text-black rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                                {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                              </span>
                            )}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              )
            })}
          </ul>

          <div className="mt-2 pt-2 border-t border-white/10">
            {secondaryNavigationItems.map((item) => {
              const Icon = item.icon

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center ${isOpen ? 'gap-3 px-3' : 'justify-center px-3'} py-2 w-full ${getActiveStyle(isActive)}`
                  }
                  onClick={handleNavClick}
                  end={item.end}
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={
                          isActive ? 'h-6 w-6 text-black' : `h-6 w-6 text-[${item.color}]`
                        }
                        style={isActive ? undefined : { color: item.color }}
                      />
                      {isOpen && <span className="transition-opacity">{item.label}</span>}
                    </>
                  )}
                </NavLink>
              )
            })}
          </div>
        </nav>
      </ScrollArea>

      {/* Profile section with dropdown - updated positioning */}
      {athlete && (
        <div className="p-4 border-t border-white/10 mt-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div
                className={`flex items-center ${isOpen ? 'gap-3' : 'justify-center'} cursor-pointer hover:bg-white/5 rounded-lg p-2 transition-colors relative`}
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage src={athlete?.avatar} alt={athlete?.name} />
                  <AvatarFallback>{athlete?.name?.charAt(0) || 'A'}</AvatarFallback>
                </Avatar>
                {isOpen && (
                  <>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white">{athlete?.name}</div>
                      <div className="text-xs text-gray-400">{getSportDisplay()}</div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="right"
              className="w-64 bg-[#2a3a45] border-2 border-[#3a4a55] rounded-2xl p-2 text-white z-50"
              sideOffset={8}
            >
              <UserMenuContent />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </aside>
  )
}
export default Sidebar
